import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import TextInput, { TEXT_INPUT_STYLES } from './TextInput';
import ctaButtonStyles from '../../css/components/buttons/button.css';
import styles from '../../css/components/forms/loginForm.css';
import { login } from '../../helpers/form-logins';
import ErrorMessage, { ERROR_MESSAGE_STYLES } from './ErrorMessage';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import { AppContext } from '../../Store';
import { ChallengeContext } from '../../challenge-context';
import { useTranslation } from 'react-i18next';
import { metrics } from '../../metrics-config';
import LoadingSpinner from '../common/LoadingSpinner';
import { LoginErrorIdentifierNotExist } from '../../api/actions/login';
import { isChallengeFailure } from '../../api/actions/challenge-failure';

function LoginForm({
	children = null,
	playerIdentifierInputId = 'n/a',
	passwordInputId = 'n/a',
	buttonId = 'n/a',
	wrapperClassName = '',
	isTabbed = false,
}) {
	const { t, i18n } = useTranslation('translation');

	const [state, dispatch] = useContext(AppContext);
	const [playerIdentifier, setPlayerIdentifier] = useState('');
	const [password, setPassword] = useState('');
	const onelpsParams = useContext(OneLPSsdkParamsContext);

	const challengePromiseRef = useContext(ChallengeContext);
	const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

	const gameId = onelpsParams.gameId;

	const clickingForgotPWLink = () => {
		dispatch({ type: 'SHOW_FORGOTPW_OVERLAY' });
	};

	const [playerIdentifierError, setPlayerIdentifierError] = useState(null);
	const [remoteErrorKey, setRemoteErrorCode] = useState('');
	const [passwordError, setPasswordError] = useState(null);
	const [dynamicLoginErrorClassName, setDynamicLoginErrorClassName] = useState('');

	const getPlayerIdentifierErrorMessage = () =>
		i18n.exists(`loginForm.playerIdentifier.errorMessages.errorMessageEmpty.${gameId}`) ?
			t(`loginForm.playerIdentifier.errorMessages.errorMessageEmpty.${gameId}`) :
			t('loginForm.playerIdentifier.errorMessages.errorMessageEmpty.generic');

	const getPlayerIdentifierPlaceholder = () =>
		i18n.exists(`loginForm.playerIdentifier.placeholder.${gameId}`) ?
			t(`loginForm.playerIdentifier.placeholder.${gameId}`) :
			t('loginForm.playerIdentifier.placeholder.generic');

	async function onLoginButtonClick() {
		setShowLoadingIndicator(true);

		if ((!playerIdentifier && !password) || !playerIdentifier) {
			setPlayerIdentifierError(getPlayerIdentifierErrorMessage() + ' ');
			setDynamicLoginErrorClassName('loginFailedErrorMessageEmpty');
			setShowLoadingIndicator(false);
		} else if (!password) {
			setPasswordError(t('loginForm.password.errorMessages.errorMessageEmpty') + ' ');
			setDynamicLoginErrorClassName('loginFailedErrorMessagePWEmpty');
			setShowLoadingIndicator(false);
		} else {
			try {
				if (challengePromiseRef.current) {
					await challengePromiseRef.current;
				}
				const loginResult = await login(playerIdentifier, password, state.rememberMe, dispatch);

				if (loginResult.errors && loginResult.errors.length !== 0) {
					setPlayerIdentifierError(t(loginResult.errors[0].code) + ' ');
					setRemoteErrorCode(loginResult.errors[0].code);
					setDynamicLoginErrorClassName(loginResult.errors[0].code);
					setShowLoadingIndicator(false);
				} else if (loginResult.redirectUrl) {
					const playerId = loginResult.playerId;
					if (playerId && metrics) {
						metrics.collectAccountLoginMetrics(playerId, loginResult.redirectUrl);
					}

					window.top.location.href = loginResult.redirectUrl;
				}
			} catch (error) {
				console.log('error with login happened: ', error);
				if (isChallengeFailure(error)) {
					setPlayerIdentifierError(t(error.errors[0].code));
					setShowLoadingIndicator(false);
					return;
				}
				setPlayerIdentifierError(error);
				setShowLoadingIndicator(false);
			}
		}
	}

	function removeAllErrors() {
		setPlayerIdentifierError(null);
		setPasswordError(null);
		setRemoteErrorCode('');
	}

	const ctaButtonClassNames = isTabbed ?
		`${ctaButtonStyles.button} ${ctaButtonStyles[onelpsParams.gameId]} ${ctaButtonStyles[onelpsParams.pageType]} ${ctaButtonStyles.loginButtonTabbed}` :
		`${styles.loginButton}`;

	// In these cases we don't want to render the password forgotten link.
	const renderPasswordForgottenLink = !([LoginErrorIdentifierNotExist].includes(remoteErrorKey));

	return (
		<form
			className={`${styles.wrapper} ${styles[gameId]} ${styles[wrapperClassName]}`}
			name={'Login Form'}
		>
			<TextInput
				type="text"
				id={playerIdentifierInputId}
				labelText={isTabbed ? getPlayerIdentifierPlaceholder(): null}
				placeholder={!isTabbed ? getPlayerIdentifierPlaceholder(): null}
				onChangeCallback={(e) => {
					setPlayerIdentifier(e.target.value);
					removeAllErrors();
				}}
				value={playerIdentifier}
				style={isTabbed ? TEXT_INPUT_STYLES.STANDARD : TEXT_INPUT_STYLES.LOGIN}
				disabled={state.loginInputsDisabled || showLoadingIndicator}
			/>
			{playerIdentifierError && (
				<div className={`${styles.errorMessageWrapper} ${styles[dynamicLoginErrorClassName]}`}>
					<ErrorMessage
						text={playerIdentifierError}
						style={ERROR_MESSAGE_STYLES.LOGIN}
						showCloseIcon={onelpsParams.loginForm.showCloseErrorIcon}
						onCloseIconClickCallback={() => removeAllErrors()}
						hasLink={onelpsParams.loginForm.showForgotPwLinkInErrorMessage && renderPasswordForgottenLink}
						linkText={t('loginForm.forgotPassword')}
						linkAction={() => clickingForgotPWLink()}
					/>
				</div>
			)}
			<TextInput
				type="password"
				id={passwordInputId}
				labelText={isTabbed ? t('loginForm.password.placeholder') : null}
				placeholder={!isTabbed ? t('loginForm.password.placeholder') : null}
				onChangeCallback={(e) => {
					setPassword(e.target.value);
					removeAllErrors();
				}}
				value={password}
				style={isTabbed ? TEXT_INPUT_STYLES.STANDARD : TEXT_INPUT_STYLES.LOGIN}
				disabled={state.loginInputsDisabled || showLoadingIndicator}
			/>
			{passwordError && (
				<div className={`${styles.errorMessageWrapper} ${styles[dynamicLoginErrorClassName]}`}>
					<ErrorMessage
						text={passwordError}
						style={ERROR_MESSAGE_STYLES.LOGIN}
						showCloseIcon={onelpsParams.loginForm.showCloseErrorIcon}
						onCloseIconClickCallback={() => removeAllErrors()}
						hasLink={onelpsParams.loginForm.showForgotPwLinkInErrorMessage}
						linkText={t('loginForm.forgotPassword')}
						linkAction={() => clickingForgotPWLink()}
					/>
				</div>
			)}
			<button
				type={'submit'}
				className={ctaButtonClassNames}
				onClick={(e) => {
					e.preventDefault();
					onLoginButtonClick();
				}}
				id={buttonId}
				disabled={showLoadingIndicator}
			>
				{t(onelpsParams.loginForm.buttonTranslationKey)}
			</button>
			{showLoadingIndicator && (
				<LoadingSpinner
					isOnCtaButton={isTabbed}
					isOnLoginFormButton={!isTabbed}
				/>
			)}
			{children}
		</form>
	);
}

LoginForm.propTypes = {
	children: PropTypes.node,
	playerIdentifierInputId: PropTypes.string,
	passwordInputId: PropTypes.string,
	buttonId: PropTypes.string,
	wrapperClassName: PropTypes.string,
	isTabbed: PropTypes.bool,
};

export default LoginForm;
